import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { COLORS, CONSTANTS } from "helpers/designConstants";

import ArticleCard from "components/shared/ArticleCard";
import SeasonChipRow from "./SeasonChipRow";

/**
 * One card per articleNumber. The card shows the SELECTED chip's record — its name, colour, image,
 * price, size range and intro date — so what the rep sees is what the public catalogue will render,
 * because the viewer renders that same record.
 *
 * Add/remove always acts on the selected chip's record and stamps that chip's season.
 */
const ProductGroupCard = ({
  group,
  selectedArticles,
  priceToShow,
  showInventoryButton,
  onZoomArticle,
  onViewInventory,
  onArticleSelection,
  addedEntryKeys,
  language
}) => {
  const [selectedSeasonKey, setSelectedSeasonKey] = useState(
    group.defaultChip.seasonKey
  );

  // A new search replaces the groups; make sure a stale chip key can't survive into a new group.
  useEffect(() => {
    setSelectedSeasonKey(group.defaultChip.seasonKey);
  }, [group.defaultChip.seasonKey, group.articleNumber]);

  const activeChip =
    group.chips.find((chip) => chip.seasonKey === selectedSeasonKey) ||
    group.defaultChip;
  const record = activeChip.record;

  // Record-based, deliberately: the card's +/- toggles the RECORD in the catalogue, and a record
  // can only be in it once. The chips' "added" marker is entry-based — see SeasonChipRow.
  const isSelected = (selectedArticles || []).some(
    (article) => article.id === record.id
  );

  return (
    <Wrapper hasChips={group.chips.length > 1}>
      <FillingArticleCard
        seasonKey={activeChip.seasonKey}
        priceToShow={priceToShow}
        article={record}
        showEditButton={false}
        showInventoryButton={showInventoryButton}
        shouldShowLimitedSoldOut
        isSelectable
        isSelected={isSelected}
        didSelectAddRemove={() => onArticleSelection(record, activeChip.seasonKey)}
        onZoomArticle={onZoomArticle}
        onViewInventory={onViewInventory}
      />
      {group.chips.length > 1 && (
        <SeasonChipRow
          chips={group.chips}
          selectedSeasonKey={activeChip.seasonKey}
          onSelectChip={(chip) => setSelectedSeasonKey(chip.seasonKey)}
          priceVaries={group.priceVaries}
          sizesVaries={group.sizesVaries}
          addedEntryKeys={addedEntryKeys}
          language={language}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${CONSTANTS.borderRadius};
  border: ${({ hasChips }) =>
    hasChips ? `1px solid ${COLORS.secondary}` : "none"};
  padding-bottom: ${({ hasChips }) => (hasChips ? "5px" : "0")};
`;

// The grid stretches every Wrapper to its row's height, but ArticleCard keeps its own natural
// height — so a card with a short name, or no chips, floated in a taller row with dead space under
// it. Growing the card to fill the wrapper makes every card in a row the same height.
// styled(ArticleCard) rather than editing ArticleCard: it already forwards `className` to its
// container, and five other consumers depend on its current sizing.
const FillingArticleCard = styled(ArticleCard)`
  flex: 1 1 auto;
`;

export default ProductGroupCard;
